import type { AuditLogEntryRep } from '@gonfalon/audit-log/types';
import type { AnyRoute } from '@gonfalon/router/generated/routeInfo';

/**
 * URL parameter name for rollback preview mode.
 */
export const ROLLBACK_URL_PARAM = 'previewId';

/**
 * Rollback context value when in rollback preview mode.
 * Entity-agnostic - works for flags, segments, experiments, etc.
 * */
export interface RollbackContextValue {
  /** The audit log entry ID being used for rollback preview */
  rollbackId: string;
  /** The full audit log entry */
  entry: AuditLogEntryRep;
  /** The version to roll back to (encapsulated selection based on entry position) */
  rollbackVersion: unknown;
  /** Exit rollback preview mode - clears URL param */
  exitRollback: () => void;

  /** Loading state for async checks */
  state: {
    isLoading: boolean;
    isError: boolean;
  };
}

/** Entity types supported by rollback */
export type RollbackEntityType = 'flag';

/** Parsed context from audit entry - generic, not rollback-specific */
export interface AuditEntryContext {
  entryId: string;
  entryDate: number;
  projectKey: string;
  environmentKey: string;
  entityType: string;
  entityKey: string;
  resourceIdentifier: string;
  actions: string[];
}

/** Config for each entity type (config-driven approach) */
export interface RollbackConfig {
  /** Entity type identifier */
  type: RollbackEntityType;
  /** Actions eligible for rollback */
  actions: readonly string[];
  /** Actions that invalidate rollback eligibility - blocks rollback even if valid actions are present */
  invalidActions?: readonly string[];
  /**
   * Whether the preview/rollback control should render for this audit entry on the current route.
   * (Navigation is handled by the caller, e.g. `onPreview` opening change history.)
   */
  shouldShowPreviewControl: (context: AuditEntryContext, route: AnyRoute | undefined) => boolean;
}

/**
 * Result of checking if rollback is disabled for an entry.
 */
export interface DisabledCheckResult {
  /** Whether rollback is disabled for this entry */
  isDisabled: boolean;
  /** Human-readable reason (for tooltip) */
  reason?: string;
  /** Loading state for async checks */
  isPending: boolean;
}
